import { planterMiniGameStyles } from "./PlanterMiniGameStyles";
import { PlanterMiniGameApi } from "./PlanterMiniGameApi";
import { PlanterMiniGameRenderer } from "./PlanterMiniGameRenderer";
import { PlanterGameElements, PlanterStatus } from "./PlanterMiniGameTypes";

/**
 * Web component for the planter mini-game
 */
export class PlanterMiniGame extends HTMLElement {
    private readonly _api: PlanterMiniGameApi = new PlanterMiniGameApi();
    private _renderer: PlanterMiniGameRenderer | null = null;
    private _waterLevel: number = 0;
    private _completedMiniGame: boolean = false;
    private _elements: PlanterGameElements = {
        waterLevelElement: null,
        waterLevelTextElement: null,
        plantElement: null,
        successMessageElement: null,
        continueButtonElement: null,
    };

    public constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    /**
     * Lifecycle callback when component is added to DOM
     */
    public connectedCallback(): void {
        console.log("PlanterMiniGame connected to DOM");

        if (this.shadowRoot) {
            this._renderer = new PlanterMiniGameRenderer(
                this.shadowRoot,
                planterMiniGameStyles,
                (): void => this.addWater(),
                (): void => void this.continuePlaying()
            );

            this._elements = this._renderer.render(this._waterLevel, this._completedMiniGame);
        }

        // Check if the mini-game is already completed in the session
        const playerSessionId: string | null = localStorage.getItem("playerSessionId");
        if (playerSessionId) {
            void this.checkGameCompletion(playerSessionId);
        }
    }

    /**
     * Check if the mini-game is already completed in the session
     */
    private async checkGameCompletion(sessionId: string): Promise<void> {
        try {
            const status: PlanterStatus = await this._api.checkGameCompletion(sessionId);

            if (status.plantWatered) {
                this._completedMiniGame = true;
                this.renderCompletedState();
            }
        }
        catch (error: unknown) {
            console.error("Error checking planter status:", error);
        }
    }

    /**
     * Add water to the plant
     */
    private addWater(): void {
        if (this._completedMiniGame) {
            return;
        }

        this._waterLevel += 10;

        if (this._waterLevel >= 100) {
            this._waterLevel = 100;
            void this.completeMiniGame();
        }

        if (this._renderer) {
            this._renderer.updateWaterLevel(this._waterLevel);
        }
    }

    /**
     * Complete the mini-game
     */
    private async completeMiniGame(): Promise<void> {
        if (this._completedMiniGame) {
            return;
        }

        this._completedMiniGame = true;

        if (this._renderer) {
            this._renderer.showSuccessMessage(
                "The plant has been watered properly. You can now examine it to find what's buried in the soil."
            );
        }

        // Show the completed state with the Continue button
        this.renderCompletedState();

        try {
            // Save progress to server
            const sessionId: string = localStorage.getItem("playerSessionId") || "";
            await this._api.saveMiniGameProgress(sessionId, true);
        }
        catch (error: unknown) {
            console.error("Error saving mini-game progress:", error);
        }
    }

    /**
     * Show the completed state
     */
    private renderCompletedState(): void {
        if (this._renderer) {
            this._elements = this._renderer.render(100, true);
        }
    }

    /**
     * Continue playing the game
     */
    private async continuePlaying(): Promise<void> {
        console.log("continuePlaying called");

        // Disable the continue button if it exists to prevent multiple clicks
        if (this._elements.continueButtonElement) {
            this._elements.continueButtonElement.disabled = true;
            this._elements.continueButtonElement.textContent = "Loading...";
        }

        try {
            // Get current session from localStorage
            const sessionId: string | null = localStorage.getItem("playerSessionId");
            if (!sessionId) {
                console.error("No player session ID found");
                window.location.reload();
                return;
            }

            // Update the session directly via the service
            await this._api.saveMiniGameProgress(sessionId, true);
        }
        catch (error: unknown) {
            console.error("Error in continuePlaying:", error);
            // Re-enable the button if there was an error
            if (this._elements.continueButtonElement) {
                this._elements.continueButtonElement.disabled = false;
                this._elements.continueButtonElement.textContent = "Continue";
            }
            return;
        }

        // Reload the page
        window.location.reload();
    }
}
